import { getOfflineDevices, getOfflineDevicesMeta } from '../common/api/devices';
import { DeviceOffline, DeviceWithVenue } from '../common/types';
import { DeviceStatusIcon } from '../common/devices';
import { PercentStatus } from '../common/percent-status';

import { DefinedQueryTableProps, QueryTable } from './QueryTable';
import { DeviceLink } from './DeviceLink';
import { VenueLink } from './VenueLink';
import { Box, Header, SpaceBetween } from '@cloudscape-design/components';
import { formatDate, formatTime, formatTimeShort } from '../common/formatting';

export type OfflineDeviceTableProps = DefinedQueryTableProps<DeviceOffline> & {
  description?: string;
};

export function OfflineDeviceTable(props: OfflineDeviceTableProps) {
  const getItems = props.getItems ?? getOfflineDevices;
  const getMeta = props.getMeta ?? getOfflineDevicesMeta;

  return (
    <QueryTable
      {...props}
      columnDefinitions={[
        {
          id: 'status',
          header: 'Status',
          cell: (e: DeviceOffline) => (
            <SpaceBetween direction="vertical" size="xxxs">
              <DeviceStatusIcon
                customType={e.device.kiosk?.priority_level ? 'error' : undefined}
                status={e.device.status}
                text={
                  e.device.kiosk?.priority_level
                    ? `- ${e.device.kiosk.priority_level}`
                    : e.device.kiosk?.known_to_be_offline
                    ? 'Known to be offline'
                    : undefined
                }
              />
              {e.time_offline && (
                <Box variant="span" color="text-body-secondary">
                  Offline for {formatTimeShort(e.time_offline)}
                </Box>
              )}
              {e.last_online && (
                <Box variant="span" color="text-body-secondary">
                  Last online {formatDate(new Date(e.last_online + 'Z'))}
                </Box>
              )}
            </SpaceBetween>
          ),
        },
        {
          id: 'device_id',
          header: 'Device & Venue',
          cell: (e: DeviceOffline) => (
            <Header
              variant="h3"
              description={
                <SpaceBetween direction="vertical" size="xxxs">
                  <VenueLink venue={e.device.venue} />

                  <PercentStatus
                    max_percent={50}
                    min_percent={10}
                    total={e.device.slots}
                    value={
                      e.device.status === 'Unknown'
                        ? undefined
                        : e.device.slots - e.device.active_slots
                    }
                  />
                </SpaceBetween>
              }
              key={e.device.uuid}
            >
              <DeviceLink device={e.device} />
            </Header>
          ),
        },
        {
          id: 'info',
          header: 'Info',
          cell: (e: DeviceOffline) => (
            <SpaceBetween direction="vertical" size="xxxs">
              {e.device.kiosk?.install_date && (
                <Box variant="span" color="text-body-secondary">
                  Installed {formatDate(new Date(e.device.kiosk.install_date + 'Z'))}
                </Box>
              )}
              {e.device.kiosk?.uninstall_date && (
                <Box variant="span" color="text-body-secondary">
                  Uninstalled {formatDate(new Date(e.device.kiosk.uninstall_date + 'Z'))}
                </Box>
              )}
              {e.device.kiosk?.sim_card && (
                <Box variant="span" color="text-body-secondary">
                  SIM: {e.device.kiosk.sim_card}
                </Box>
              )}
              {e.device.kiosk?.processing_company && (
                <Box variant="span" color="text-body-secondary">
                  Processing Company: {e.device.kiosk.processing_company}
                </Box>
              )}
            </SpaceBetween>
          ),
        },
      ]}
      description={props.description ?? 'High-priority Zappy devices that are offline'}
      endpointName="devices"
      getItems={getItems}
      getMeta={getMeta}
      resourceName="Offline Devices"
      hideDateRange
      hideFilter
    />
  );
}
